import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'

import { useIntersectionContext } from 'marketing-site/src/components/context/IntersectionContext'
import { useVerticalObserver } from 'marketing-site/src/library/hooks/use-vertical-observer'
import { Dropdown } from '../../elements/Dropdown'
import { IProps } from './index'
import {
  IProps as ISecondaryNavItem,
  SubNavItem,
} from 'marketing-site/src/mvp/library/components/SubNavItem'
import { Text } from 'marketing-site/src/library/elements/Text'

export const SubNav: React.FC<IProps> = ({ navTitle, navItems }) => {
  const router = useRouter()
  const { translate } = useTranslate()

  const [activePanel, setActivePanel] = useState<number>(0)

  const { currentItem } = useIntersectionContext()

  useVerticalObserver()

  const urlWithoutQuery = router.asPath.split('?')[0]

  useEffect(() => {
    if (currentItem) {
      const index = navItems.findIndex((element) => {
        return element && element.url.split('#')[1] === currentItem
      })

      Number.isInteger(index) && index !== -1 && setActivePanel(index)
    }
  }, [currentItem, navItems])

  return (
    <>
      <div className="sub-nav grid-layout">
        <div className="sub-nav__select-list" data-testid="subnav">
          {!!navTitle && <Text size="body">{`${translate(navTitle)}:`}</Text>}
          <Dropdown
            placeholder={navItems[activePanel].title}
            onSelect={(index: number) => {
              setActivePanel(index)
              window.location.replace(navItems[index].url)
            }}
          >
            {navItems.map((panel) => (
              <Text key={panel.title} size="body">
                {panel.title}
              </Text>
            ))}
          </Dropdown>
        </div>
        <ul className="sub-nav__full-list">
          {!!navTitle && (
            <li className="sub-nav__item sub-nav__nav-title" data-testid="subnav-title">
              {navTitle}:
            </li>
          )}
          {navItems.map((navItem: ISecondaryNavItem) => {
            const { title } = navItem
            const isSelected = urlWithoutQuery === navItem.url || navItem.url === `#${currentItem}`
            return (
              <li key={title} className="sub-nav__item" data-testid="subnav-item">
                <SubNavItem {...navItem} isActive={isSelected} />
              </li>
            )
          })}
        </ul>
      </div>

      <style jsx>{`
        .sub-nav {
          box-shadow: inset 0px 6px 6px -2px var(--neutral-200);
          background: var(--paper);

          &__select-list {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            width: 100%;
            z-index: 200;
            padding-top: 1.25em;
            padding-bottom: 1rem;

            & > :global(span) {
              color: var(--mvp-slate);
              white-space: nowrap;
            }

            :global(.dropdown) {
              flex-basis: 100%;
            }
          }

          &__full-list {
            display: none;
          }

          &__item {
            padding-bottom: 2rem;
          }
        }

        @include media-query(tablet) {
          .sub-nav {
            &__select-list {
              padding-top: 1em;

              :global(.dropdown) {
                max-width: 12.5rem;
              }
            }
          }
        }

        @include media-query(desktop) {
          .sub-nav {
            &__full-list {
              display: block;
              max-width: 1240px;
              margin: 0 auto;
              display: flex;
              gap: 1.5rem;
              align-items: baseline;
            }

            &__select-list {
              display: none;
            }

            &__item {
              padding: 1rem 0;
              flex-basis: auto;
            }

            &__nav-title {
              text-align: center;
              flex-basis: max-content;
              padding: 1rem 0;
            }
          }
        }
      `}</style>
    </>
  )
}
